import React, { Dispatch, SetStateAction, useCallback, useState } from "react";
import { setLocalStorageItemManaged } from "src/storage/localStorageUtils.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { AavoEnvironment } from "src/api/generated/io/aavo/shared/aavoEnvironment";

export type StoredStateType = string | number | boolean | object | null;

export type StoredStateTypeLiteral = "string" | "number" | "boolean" | "object" | "array";

export interface UseStoredStateParams<T extends StoredStateType> {
	defaultValue: T;
	key: string;
	type?: StoredStateTypeLiteral;
	separateByEnvironmentType?: boolean;
}

export const useStoredState = <T extends StoredStateType>({
	defaultValue,
	key: keyProp,
	type,
	separateByEnvironmentType = false,
}: UseStoredStateParams<T>): [state: T, setState: React.Dispatch<React.SetStateAction<T>>] => {
	const { environmentType } = useGlobalInitData();
	const fullKey = getFullKey(keyProp, separateByEnvironmentType, environmentType);

	const parse = (serialized: string | null): T => {
		if (serialized === null) return null as T;
		switch (type ?? typeof defaultValue) {
			case "string":
				return serialized as T;
			case "number":
				return Number(serialized) as T;
			case "boolean":
				return (serialized === "true") as T;
			default: {
				try {
					return JSON.parse(serialized);
				} catch (error) {
					console.error(`Error parsing stored state '${fullKey}'`, error);
					localStorage.removeItem(fullKey); // Remove corrupted item from storage
					return defaultValue;
				}
			}
		}
	};

	const currentState = parse(localStorage.getItem(fullKey)) ?? defaultValue;

	const [state, setState] = useState<T>(currentState);

	const serialize = (value: T): string | null => {
		if (value === null) return null;

		switch (type ?? typeof value) {
			case "string":
				return value as string;
			case "number":
				return String(value);
			case "boolean":
				return (value as boolean) ? "true" : "false";
			default:
				// 'object' or 'array' — Exception handling for circular reference errors
				try {
					return JSON.stringify(value);
				} catch (e) {
					console.error(e);
					return null;
				}
		}
	};

	const setStoredState: Dispatch<SetStateAction<T>> = useCallback(
		(valueOrFunction) => {
			if (valueOrFunction instanceof Function) {
				setState((prev) => {
					const newValue = valueOrFunction(prev);
					updateStorageItem(fullKey, newValue, serialize);
					return newValue;
				});
			} else {
				updateStorageItem(fullKey, valueOrFunction, serialize);
				setState(valueOrFunction);
			}
		},
		[fullKey, setState],
	);

	return [state, setStoredState];
};

const getFullKey = (
	key: string,
	separateByEnvironmentType: boolean | undefined,
	environmentType: AavoEnvironment,
) => {
	if (!separateByEnvironmentType) return key;
	if (environmentType === "PROD") return key;
	return `${environmentType}-${key}`;
};

const updateStorageItem = <T extends StoredStateType>(
	key: string,
	value: T,
	serialize: (value: T) => string | null,
) => {
	const serialized = serialize(value);
	if (serialized === null) {
		localStorage.removeItem(key);
	} else {
		setLocalStorageItemManaged(key, serialized);
	}
};;
